'use client';

import type { RevocableOrder } from 'data-layer';
import { useEffect, useState } from 'react';
import { useUIStore } from 'stores/ui';
import { logger } from 'utils';
import { OrdersMultiSelect, ProductMultiSelection } from './partials';
import { getRevocableOrdersAction } from '../../../actions/getRevocableOrdersAction';
import { CLIENT_AUTH_STATUS, useClientAuthentication } from 'hooks';
import type { CanceledOrder } from '../../../types';
import styles from './CancellationFieldsClient.module.css';

export interface CancellationFieldsClientProps {
  constants: Record<string, string>;
  locale: string;
}

/**
 * Client component managing order and product selection state for the cancellation topic.
 * Loads the revocable orders in the browser once the client session is known and coordinates selection
 * state across the OrdersMultiSelect and ProductMultiSelection dropdowns.
 * Serialises the final selection as a hidden form field consumed by submitContactFormAction on submit.
 *
 * @param props.constants - CMS i18n strings for all cancellation field labels and placeholders
 */
export function CancellationFieldsClient(props: CancellationFieldsClientProps) {
  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);
  const [canceledOrders, setCanceledOrders] = useState<CanceledOrder[]>([]);
  const [revocableOrders, setRevocableOrders] = useState<RevocableOrder[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const { constants, locale } = props;
  const { status, isAuthenticated, uuid } = useClientAuthentication();
  const isLoading = status === CLIENT_AUTH_STATUS.LOADING || isLoadingOrders;

  useEffect(() => {
    if (!isAuthenticated || !uuid) {
      setRevocableOrders([]);
      setIsLoadingOrders(false);

      return;
    }

    // A response for a previous uuid or an unmounted component must not overwrite newer state.
    let cancelled = false;
    setIsLoadingOrders(true);

    getRevocableOrdersAction()
      .catch(error => {
        logger.warn({ error }, 'Failed to load revocable orders');

        return [];
      })
      .then(orders => {
        if (cancelled) return;

        setRevocableOrders(orders);
        setIsLoadingOrders(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, uuid]);

  const { contactForm, setCancellationErrors } = useUIStore(state => state);
  const { cancellationErrors } = contactForm;

  const handleOrderSelectionChange = (orderIds: number[]) => {
    setCancellationErrors({ productsError: cancellationErrors.productsError });
    setSelectedOrderIds(orderIds);
    setCanceledOrders(prev => prev.filter(co => orderIds.includes(co.orderId)));
  };

  const handleProductChange = (orders: CanceledOrder[]) => {
    setCancellationErrors({ orderError: cancellationErrors.orderError });
    setCanceledOrders(orders);
  };

  const selectedOrders = revocableOrders.filter(o => selectedOrderIds.includes(o.userOrderId));

  return (
    <div className={styles.container}>
      <input type="hidden" name="canceledOrders" value={JSON.stringify(canceledOrders)} />
      <input type="hidden" name="selectedOrderIds" value={JSON.stringify(selectedOrderIds)} />
      <div className={styles.inputContainer}>
        <div className={styles.inputWrapper}>
          <OrdersMultiSelect
            key={String(isAuthenticated)}
            disabled={!isAuthenticated}
            loading={isLoading}
            fallbackText={constants.i18n_cancel_orders_fallback_text}
            label={constants.i18n_cancel_orders_label}
            placeholder={constants.i18n_cancel_orders_placeholder_authenticated}
            summaryCounts={constants.i18n_cancel_orders_summary}
            orderItemLabel={constants.i18n_cancel_order_item_label}
            revocableOrders={revocableOrders}
            selectedOrderIds={selectedOrderIds}
            locale={locale}
            onChange={handleOrderSelectionChange}
            helperText={cancellationErrors.orderError}
            hasError={!!cancellationErrors.orderError}
          />
        </div>
        <div className={styles.inputWrapper}>
          <ProductMultiSelection
            key={String(isAuthenticated)}
            disabled={!isAuthenticated}
            loading={isLoading}
            fallbackText={constants.i18n_cancel_products_fallback_text}
            label={constants.i18n_cancel_products_label}
            placeholder={constants.i18n_cancel_products_placeholder_authenticated}
            summaryCounts={constants.i18n_cancel_products_summary}
            orderItemLabel={constants.i18n_cancel_order_item_label}
            orders={selectedOrders}
            canceledOrders={canceledOrders}
            onChange={handleProductChange}
            helperText={cancellationErrors.productsError}
            hasError={!!cancellationErrors.productsError}
          />
        </div>
      </div>
    </div>
  );
}
